import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { PageHeader } from "./ui";

const ENTITLEMENT_LABELS = {
  company_profile: "Company profiles",
  contact_email: "Contact email unlocks",
  contact_phone: "Contact phone unlocks",
  export: "Exports",
  saved_companies: "Saved companies",
};

export default function AdminPlans() {
  const [plans, setPlans] = useState(null);
  const [edits, setEdits] = useState({});
  const [working, setWorking] = useState("");

  const load = useCallback(() => {
    api.get("/admin/membership-plans").then((r) => {
      setPlans(r.data);
      setEdits(Object.fromEntries(r.data.map((p) => [p.name, { price: p.price, unlock_credits: p.unlock_credits, entitlements: { ...p.entitlements } }])));
    }).catch(() => toast.error("Could not load plans"));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (name) => {
    setWorking(name);
    try {
      await api.put(`/admin/membership-plans/${name}`, edits[name]);
      toast.success(`${name} plan updated`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setWorking(""); }
  };

  if (!plans) return <div className="py-16 text-center text-[12px] font-mono uppercase tracking-widest text-[#7C8AA3] animate-pulse" data-testid="plans-loading">Loading plans…</div>;

  return (
    <div data-testid="page-plans">
      <PageHeader kicker="Membership" title="MEMBERSHIP PLANS" sub="All pricing, unlock credits and entitlements are configurable here. Changes apply to checkout and the entitlement engine immediately — every change is audit-logged." />
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {plans.map((p) => {
          const e = edits[p.name] || {};
          return (
            <div key={p.name} className="adm-card p-5" data-testid={`plan-card-${p.name.toLowerCase()}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-[15px] font-bold text-[#0B1B33]">{p.name}</h3>
                {p.featured && <span className="adm-pill text-violet-700 border-violet-300 bg-violet-50">Featured</span>}
              </div>
              <label className="adm-kicker !text-[#7C8AA3] block mb-1.5">Price (display)</label>
              <input value={e.price ?? ""} onChange={(ev) => setEdits({ ...edits, [p.name]: { ...e, price: ev.target.value } })}
                data-testid={`plan-price-${p.name.toLowerCase()}`} className="adm-input mb-3" />
              <label className="adm-kicker !text-[#7C8AA3] block mb-1.5">Monthly Contact Unlock Credits</label>
              <input type="number" min="0" max="9999" value={e.unlock_credits ?? 0}
                onChange={(ev) => setEdits({ ...edits, [p.name]: { ...e, unlock_credits: Number(ev.target.value) } })}
                data-testid={`plan-credits-${p.name.toLowerCase()}`} className="adm-input mb-4" />
              <p className="adm-kicker !text-[#7C8AA3] mb-2">Entitlements</p>
              <div className="space-y-1.5 mb-5">
                {Object.entries(ENTITLEMENT_LABELS).map(([k, label]) => (
                  <label key={k} className="flex items-center gap-2.5 text-[12px] text-[#43546D] cursor-pointer">
                    <input type="checkbox" checked={!!e.entitlements?.[k]}
                      onChange={(ev) => setEdits({ ...edits, [p.name]: { ...e, entitlements: { ...e.entitlements, [k]: ev.target.checked } } })}
                      data-testid={`plan-ent-${k.replace(/_/g, "-")}-${p.name.toLowerCase()}`}
                      className="w-3.5 h-3.5 accent-[#2563EB]" />
                    {label}
                  </label>
                ))}
              </div>
              <button onClick={() => save(p.name)} disabled={!!working} data-testid={`plan-save-${p.name.toLowerCase()}`} className="adm-btn w-full">
                {working === p.name ? <Loader2 size={13} className="animate-spin" /> : <><Check size={13} /> Save {p.name}</>}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
